(() => {
  'use strict';

  const DRAWER_SELECTOR = 'cart-drawer';
  const HEADER_TRIGGER_SELECTOR = '[data-frenly-cart-trigger], #cart-icon-bubble, a.header__icon--cart';
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    'iframe',
    'shopify-accelerated-checkout',
    'shopify-accelerated-checkout-cart',
    'summary',
  ].join(',');
  const SECTION_IDS = ['cart-icon-bubble', 'main-cart-items', 'main-cart-footer'];

  const normalizePath = (value) => {
    try {
      const path = new URL(value, window.location.origin).pathname;
      return path.length > 1 ? path.replace(/\/+$/, '') : path;
    } catch (_error) {
      return '';
    }
  };

  const safeJson = async (response) => {
    const text = await response.text();
    if (!text) return {};
    try {
      return JSON.parse(text);
    } catch (_error) {
      return { description: text };
    }
  };

  const escapeSelectorValue = (value) => {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(String(value));
    return String(value).replace(/["\\]/g, '\\$&');
  };

  class FrenlyCartDrawer extends HTMLElement {
    constructor() {
      super();
      this.previouslyFocused = null;
      this.inertState = [];
      this.closeTimer = null;
      this.recommendationRequest = 0;
      this.openSequence = 0;
      this.walletExposureSequence = 0;
      this.walletTimer = null;
      this.walletObserver = null;
      this.boundClick = this.onClick.bind(this);
      this.boundChange = this.onChange.bind(this);
      this.boundKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.boundClick);
      this.addEventListener('change', this.boundChange);
      this.addEventListener('keydown', this.boundKeydown);
      this.syncPurchaseSurfaceClass();
      this.syncHeaderTriggers(false);

      if (this.isEnabled) {
        window.FrenlyCartDrawer = {
          enabled: true,
          open: (trigger) => this.open(trigger),
          close: () => this.close(),
          refresh: () => this.refreshSections(),
        };
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.boundClick);
      this.removeEventListener('change', this.boundChange);
      this.removeEventListener('keydown', this.boundKeydown);
      window.clearTimeout(this.closeTimer);
      this.clearWalletWatcher();
      this.recommendationRequest += 1;
      document.body.classList.remove('frenly-drawer-open');
      document.body.classList.remove('frenly-purchase-suppressed');
      this.syncHeaderTriggers(false);
      this.setBackgroundInert(false);
    }

    get isEnabled() {
      return this.dataset.enabled === 'true';
    }

    get isOpen() {
      return !this.hidden && this.dataset.open === 'true';
    }

    get dialog() {
      return this.querySelector('.frenly-cart-drawer__dialog');
    }

    get sectionId() {
      return this.dataset.sectionId || 'frenly-cart-drawer';
    }

    get requestedSectionIds() {
      return [this.sectionId, ...SECTION_IDS].filter((id, index, ids) => id && ids.indexOf(id) === index).slice(0, 5);
    }

    // Dawn 9 product-form.js consumes this public interface. Keeping the
    // standard <cart-drawer> contract avoids modifying product-form.js and
    // preserves quick-add behaviour.
    getSectionsToRender() {
      return this.requestedSectionIds.map((id) => ({ id }));
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      return parsed.querySelector(selector)?.innerHTML || '';
    }

    setActiveElement(element) {
      this.activeElement = element;
      this.previouslyFocused = element;
    }

    async renderContents(parsedState) {
      const trigger = this.activeElement || document.activeElement;
      try {
        const rendered = await this.renderSections(parsedState?.sections, 'drawer-close');
        if (!rendered) await this.refreshSections('drawer-close');
        this.open(trigger, { loadRecommendations: false });
        this.announce('Item added to your bag.');
        this.emitCommerceEvent('cart_add_complete', {
          threshold_state: this.dataset.thresholdState || 'unknown',
        });
      } catch (_error) {
        // product-form.js owns its inline product error. This message covers a
        // rare successful add followed by a failed section refresh.
        this.open(trigger);
        this.showError('The item was added, but the bag could not refresh. Open the cart page to review it.');
      }
    }

    syncPurchaseSurfaceClass() {
      const onCartPage = normalizePath(window.location.pathname) === normalizePath(this.dataset.cartUrl);
      document.body.classList.toggle(
        'frenly-cart-surface',
        this.dataset.suppressPurchaseOverlays === 'true' && onCartPage
      );
    }

    syncHeaderTriggers(expanded) {
      document.querySelectorAll(HEADER_TRIGGER_SELECTOR).forEach((trigger) => {
        if (!this.isEnabled) {
          trigger.removeAttribute('aria-controls');
          trigger.removeAttribute('aria-haspopup');
          trigger.removeAttribute('aria-expanded');
          return;
        }
        trigger.setAttribute('aria-controls', 'FrenlyCartDrawer-Dialog');
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-expanded', String(expanded));
      });
    }

    open(trigger, { loadRecommendations = true } = {}) {
      if (!this.isEnabled) return false;
      window.clearTimeout(this.closeTimer);

      const isOpening = this.dataset.opening === 'true';
      if (!this.isOpen && !isOpening) {
        this.previouslyFocused = trigger instanceof HTMLElement ? trigger : document.activeElement;
        this.hidden = false;
        this.dataset.opening = 'true';
        this.setAttribute('aria-hidden', 'false');
        document.body.classList.add('frenly-drawer-open');
        document.body.classList.toggle(
          'frenly-purchase-suppressed',
          this.dataset.suppressPurchaseOverlays === 'true'
        );
        this.setBackgroundInert(true);
        this.syncHeaderTriggers(true);
        this.openSequence += 1;

        window.requestAnimationFrame(() => {
          if (this.hidden || this.dataset.opening !== 'true') return;
          this.dataset.open = 'true';
          delete this.dataset.opening;
          window.requestAnimationFrame(() => this.focusInitialControl());
        });

        this.emitCommerceEvent('drawer_open', {
          threshold_state: this.dataset.thresholdState || 'unknown',
        });
      }

      if (loadRecommendations) this.loadRecommendations();
      this.initializeAcceleratedCheckout();
      return true;
    }

    close({ restoreFocus = true } = {}) {
      if (this.hidden) return;
      this.dataset.open = 'false';
      delete this.dataset.opening;
      this.syncHeaderTriggers(false);
      document.body.classList.remove('frenly-drawer-open');
      document.body.classList.remove('frenly-purchase-suppressed');
      this.setBackgroundInert(false);

      this.closeTimer = window.setTimeout(() => {
        this.hidden = true;
        this.setAttribute('aria-hidden', 'true');
        if (restoreFocus && this.previouslyFocused instanceof HTMLElement && this.previouslyFocused.isConnected) {
          this.previouslyFocused.focus({ preventScroll: true });
        }
      }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 220);
    }

    focusInitialControl() {
      const preferred = this.querySelector('button[data-frenly-drawer-close]');
      (preferred || this.dialog)?.focus({ preventScroll: true });
    }

    setBackgroundInert(makeInert) {
      const drawerHost = Array.from(document.body.children).find((child) => child.contains(this));

      if (makeInert) {
        if (this.inertState.length) return;
        this.inertState = [];
        Array.from(document.body.children).forEach((child) => {
          if (child === drawerHost) return;
          this.inertState.push({ element: child, alreadyInert: child.hasAttribute('inert') });
          child.setAttribute('inert', '');
        });
        return;
      }

      this.inertState.forEach(({ element, alreadyInert }) => {
        if (!element.isConnected || alreadyInert) return;
        element.removeAttribute('inert');
      });
      this.inertState = [];
    }

    onKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab' || !this.dialog) return;
      const controls = Array.from(this.dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (element) => !element.hasAttribute('hidden') && element.getClientRects().length > 0
      );

      if (!controls.length) {
        event.preventDefault();
        this.dialog.focus();
        return;
      }

      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onClick(event) {
      const closeControl = event.target.closest('[data-frenly-drawer-close]');
      if (closeControl) {
        event.preventDefault();
        this.close();
        return;
      }

      const removeControl = event.target.closest('[data-cart-remove]');
      if (removeControl) {
        event.preventDefault();
        this.changeLine(Number(removeControl.dataset.line), 0, null);
        return;
      }

      const stepControl = event.target.closest('[data-quantity-step]');
      if (stepControl) {
        event.preventDefault();
        const container = stepControl.closest('[data-quantity-control]');
        const input = container?.querySelector('[data-cart-quantity]');
        if (!input) return;

        const step = Number(input.step) || 1;
        const current = Number(input.value) || Number(input.min) || 1;
        const next = stepControl.dataset.quantityStep === 'plus' ? current + step : current - step;
        const min = Number(input.min) || 1;
        const max = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
        const quantity = Math.max(min, Math.min(max, next));

        if (quantity !== current) {
          this.changeLine(Number(input.dataset.line), quantity, stepControl.dataset.focusKey);
        }
        return;
      }

      if (event.target.closest('[data-frenly-wallets]')) {
        this.emitCommerceEvent('wallet_click', { placement: 'cart_drawer' });
      }
    }

    onChange(event) {
      const input = event.target.closest('[data-cart-quantity]');
      if (!input) return;

      const min = Number(input.min) || 1;
      const max = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
      const requested = Number.parseInt(input.value, 10);
      const quantity = Number.isFinite(requested) ? Math.max(min, Math.min(max, requested)) : min;
      input.value = String(quantity);
      this.changeLine(Number(input.dataset.line), quantity, input.dataset.focusKey);
    }

    async handleAddForm(form, submitter) {
      if (!this.isEnabled || this.dataset.busy === 'true') return;

      const formData = new FormData(form);
      const isRecommendation = form.matches('[data-frenly-recommendation-form]');
      const addOnProductId = submitter?.dataset.addonProductId || null;

      formData.set('sections', this.requestedSectionIds.join(','));
      formData.set('sections_url', `${window.location.pathname}${window.location.search}`);

      this.open(submitter || form, { loadRecommendations: false });
      this.clearErrors();
      this.setBusy(true);
      this.setSubmitterLoading(submitter, true);

      try {
        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
        });
        const payload = await safeJson(response);

        if (!response.ok || payload.status) {
          throw this.createCartError(payload, response.status, 'add');
        }

        // The cart template's footer blocks belong to its dynamic section ID.
        // A generic section render omits those configured blocks, so refresh
        // the cart page after a successful add-on instead of replacing its
        // subtotal and checkout surface with an incomplete response.
        if (isRecommendation && normalizePath(window.location.pathname) === normalizePath(this.dataset.cartUrl)) {
          window.location.reload();
          return;
        }

        const rendered = await this.renderSections(payload.sections, 'drawer-close');
        if (!rendered) await this.refreshSections('drawer-close');

        this.announce(isRecommendation ? 'Added to your bag.' : 'Item added to your bag.');
        this.emitCommerceEvent(isRecommendation ? 'addon_add' : 'cart_add_complete', {
          product_id: addOnProductId,
          threshold_state: this.dataset.thresholdState || 'unknown',
        });
      } catch (error) {
        this.showError(error.userMessage || 'We could not add that item. Please try again.');
        this.announce('Item was not added. Please review the cart error.');
        this.loadRecommendations();
        this.emitCommerceEvent('cart_validation_error', {
          category: error.category || 'add_unknown',
        });
      } finally {
        this.setBusy(false);
        this.setSubmitterLoading(submitter, false);
      }
    }

    async changeLine(line, quantity, focusKey) {
      if (!Number.isInteger(line) || line < 1 || this.dataset.busy === 'true') return;
      this.clearErrors();
      this.setBusy(true);

      try {
        const response = await fetch(this.dataset.cartChangeUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            line,
            quantity,
            sections: this.requestedSectionIds,
            sections_url: `${window.location.pathname}${window.location.search}`,
          }),
        });
        const payload = await safeJson(response);

        if (!response.ok || payload.status) {
          throw this.createCartError(payload, response.status, 'quantity');
        }

        const rendered = await this.renderSections(payload.sections, focusKey || 'drawer-close');
        if (!rendered) await this.refreshSections(focusKey || 'drawer-close');

        this.announce(quantity === 0 ? 'Item removed from your bag.' : 'Bag quantity updated.');
        this.emitCommerceEvent(quantity === 0 ? 'cart_remove' : 'cart_quantity_update', {
          threshold_state: this.dataset.thresholdState || 'unknown',
        });
      } catch (error) {
        this.showLineError(line, error.userMessage || 'We could not update that quantity.');
        this.announce('Bag was not updated. Please review the item error.');
        this.emitCommerceEvent('cart_validation_error', {
          category: error.category || 'quantity_unknown',
        });
      } finally {
        this.setBusy(false);
      }
    }

    createCartError(payload, status, operation) {
      const payloadErrors = typeof payload.errors === 'string'
        ? payload.errors
        : payload.errors && typeof payload.errors === 'object'
          ? Object.values(payload.errors).flat().filter(Boolean).join(' ')
          : '';
      const message = payload.description || payloadErrors || payload.message || 'Cart request failed';
      const error = new Error(message);
      error.userMessage = message === 'Cart request failed' ? 'Something went wrong. Please try again.' : message;
      error.category = `${operation}_${status || payload.status || 'unknown'}`;
      return error;
    }

    async refreshSections(focusKey) {
      const url = new URL(this.dataset.cartUrl, window.location.origin);
      url.searchParams.set('sections', this.requestedSectionIds.join(','));
      const response = await fetch(url.toString(), {
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      });
      const payload = await safeJson(response);
      if (!response.ok) throw this.createCartError(payload, response.status, 'refresh');
      return this.renderSections(payload, focusKey);
    }

    async renderSections(sections, focusKey) {
      if (!sections || !sections[this.sectionId]) return false;

      const previousThresholdState = this.dataset.thresholdState;
      const wasOpen = this.isOpen || this.dataset.opening === 'true';
      const drawerRendered = this.renderDrawerSection(sections[this.sectionId], focusKey, wasOpen);
      if (!drawerRendered) return false;

      SECTION_IDS.forEach((sectionId) => {
        if (sections[sectionId]) this.renderExternalSection(sectionId, sections[sectionId]);
      });

      this.syncHeaderTriggers(wasOpen);
      this.initializeAcceleratedCheckout();
      this.loadRecommendations();

      if (previousThresholdState !== this.dataset.thresholdState) {
        this.emitCommerceEvent('shipping_threshold_state', {
          previous_state: previousThresholdState || 'unknown',
          threshold_state: this.dataset.thresholdState || 'unknown',
        });
      }
      return true;
    }

    renderDrawerSection(html, focusKey, wasOpen) {
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const incoming = documentFragment.querySelector(DRAWER_SELECTOR);
      if (!incoming) return false;

      this.innerHTML = incoming.innerHTML;
      Array.from(this.attributes)
        .filter((attribute) => attribute.name.startsWith('data-'))
        .forEach((attribute) => this.removeAttribute(attribute.name));
      Array.from(incoming.attributes)
        .filter((attribute) => attribute.name.startsWith('data-'))
        .forEach((attribute) => this.setAttribute(attribute.name, attribute.value));

      if (wasOpen) {
        this.hidden = false;
        this.dataset.open = 'true';
        delete this.dataset.opening;
        this.setAttribute('aria-hidden', 'false');
        window.requestAnimationFrame(() => this.restoreDrawerFocus(focusKey));
      }
      return true;
    }

    renderExternalSection(sectionId, html) {
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');

      if (sectionId === 'cart-icon-bubble') {
        const current = document.getElementById('cart-icon-bubble');
        const incomingWrapper = documentFragment.querySelector('.shopify-section');
        if (current && incomingWrapper) current.innerHTML = incomingWrapper.innerHTML;
        return;
      }

      const currentWrapper = document.getElementById(`shopify-section-${sectionId}`);
      const incomingWrapper = documentFragment.getElementById(`shopify-section-${sectionId}`) ||
        documentFragment.querySelector('.shopify-section');
      if (currentWrapper && incomingWrapper) currentWrapper.innerHTML = incomingWrapper.innerHTML;
    }

    restoreDrawerFocus(focusKey) {
      if (!this.isOpen) return;
      let target = null;
      if (focusKey) {
        target = this.querySelector(`[data-focus-key="${escapeSelectorValue(focusKey)}"]`);
      }
      (target || this.querySelector('[data-frenly-drawer-close]') || this.dialog)?.focus({ preventScroll: true });
    }

    setBusy(busy) {
      this.dataset.busy = String(busy);
      this.dialog?.setAttribute('aria-busy', String(busy));
      this.querySelector('.frenly-cart-drawer__form')?.toggleAttribute('inert', busy);
      this.querySelectorAll('button, input').forEach((control) => {
        if (control.matches('[data-frenly-drawer-close]')) return;
        if (busy) {
          control.dataset.frenlyWasDisabled = String(control.disabled);
          control.disabled = true;
        } else if (control.dataset.frenlyWasDisabled !== undefined) {
          control.disabled = control.dataset.frenlyWasDisabled === 'true';
          delete control.dataset.frenlyWasDisabled;
        }
      });
    }

    setSubmitterLoading(submitter, loading) {
      if (!(submitter instanceof HTMLButtonElement)) return;
      submitter.classList.toggle('loading', loading);
      submitter.setAttribute('aria-disabled', String(loading));
    }

    clearErrors() {
      this.querySelectorAll('[data-frenly-cart-error], [data-line-error]').forEach((error) => {
        error.hidden = true;
        error.textContent = '';
      });
      this.querySelectorAll('[data-cart-quantity][aria-invalid="true"]').forEach((input) => {
        input.removeAttribute('aria-invalid');
      });
    }

    showError(message) {
      const error = this.querySelector('[data-frenly-cart-error]');
      if (!error) return;
      error.textContent = message;
      error.hidden = false;
      error.focus?.();
    }

    showLineError(line, message) {
      const error = this.querySelector(`[data-line-error="${line}"]`);
      if (!error) return this.showError(message);
      error.textContent = message;
      error.hidden = false;
      this.querySelector(`[data-cart-quantity][data-line="${line}"]`)?.setAttribute('aria-invalid', 'true');
    }

    announce(message) {
      const region = this.querySelector('[data-frenly-cart-live-region]');
      if (!region) return;
      region.textContent = '';
      window.setTimeout(() => {
        region.textContent = message;
      }, 50);
    }

    async loadRecommendations() {
      const container = this.querySelector('[data-frenly-recommendations]');
      const productId = this.dataset.recommendationProductId;
      if (!container || this.dataset.recommendationsEnabled !== 'true' || !productId) return;

      const requestId = ++this.recommendationRequest;
      const section = container.closest('.frenly-cart-drawer__recommendations');
      const recommendationQuery = (this.dataset.recommendationQuery || '').trim();
      const recommendationLabel = (this.dataset.recommendationLabel || recommendationQuery).trim();
      if (section) section.hidden = false;
      container.innerHTML = '<p class="frenly-cart-drawer__recommendations-loading">Loading suggestions…</p>';

      if (recommendationQuery && this.dataset.searchUrl) {
        const searchUrl = new URL(this.dataset.searchUrl, window.location.origin);
        searchUrl.searchParams.set('q', recommendationQuery);
        searchUrl.searchParams.set('type', 'product');
        searchUrl.searchParams.set('options[prefix]', 'last');
        searchUrl.searchParams.set('section_id', 'frenly-search-recommendations');

        try {
          const response = await fetch(searchUrl.toString(), {
            headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
          });
          if (!response.ok) throw new Error('Search recommendations request failed');
          const html = await response.text();
          if (requestId !== this.recommendationRequest) return;

          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          const incoming = documentFragment.querySelector('[data-frenly-search-cart-recommendations-inner]');
          const normalizeMatchText = (value) =>
            value
              .toLowerCase()
              .normalize('NFKD')
              .replace(/[\u0300-\u036f]/g, '')
              .replace(/[^a-z0-9]+/g, ' ')
              .trim();
          const franchiseKey = normalizeMatchText(recommendationLabel);
          const incomingRecommendations = Array.from(incoming?.querySelectorAll('.frenly-cart-recommendation') || []);
          const specificRecommendations = incomingRecommendations.filter((item) => {
            const itemTitle = item.querySelector('.frenly-cart-recommendation__details a');
            const normalizedTitle = normalizeMatchText(itemTitle?.textContent || '');
            if (franchiseKey === 'horror') {
              const genuineHorrorTerms = [
                'terrifier',
                'art the clown',
                'the nun',
                'leatherface',
                'texas chainsaw',
                'the exorcist',
                'regan',
                'hannibal lecter',
                'silence of the lambs',
                'ghost face',
                'scream',
                'the conjuring',
                'alien',
                'ripley',
                'orphan black',
                'helena bloody',
              ];
              return genuineHorrorTerms.some((term) => normalizedTitle.includes(term));
            }
            return normalizedTitle.includes(franchiseKey);
          });
          if (incoming && specificRecommendations.length) {
            incomingRecommendations.forEach((item) => {
              if (!specificRecommendations.includes(item)) item.remove();
            });
            container.innerHTML = incoming.innerHTML;
            const title = section?.querySelector('[data-frenly-recommendations-title]');
            const subheading = section?.querySelector('[data-frenly-recommendations-subheading]');
            if (title) title.textContent = `More ${recommendationLabel}`;
            if (subheading) {
              subheading.textContent = specificRecommendations.length >= 3
                ? 'Same franchise, available now.'
                : 'Closest same-franchise matches available now.';
            }
            return;
          }
        } catch (_error) {
          if (requestId !== this.recommendationRequest) return;
        }
      }

      const url = new URL(this.dataset.recommendationsUrl, window.location.origin);
      url.searchParams.set('section_id', 'frenly-cart-recommendations');
      url.searchParams.set('product_id', productId);
      url.searchParams.set('limit', '10');
      url.searchParams.set('intent', 'complementary');

      try {
        const response = await fetch(url.toString(), {
          headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) throw new Error('Recommendations request failed');
        const html = await response.text();
        if (requestId !== this.recommendationRequest) return;

        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const incoming = documentFragment.querySelector('[data-frenly-recommendations-inner]');
        const hasProducts = incoming?.querySelector('.frenly-cart-recommendation');
        if (!incoming || !hasProducts) {
          if (section) section.hidden = true;
          container.innerHTML = '';
          return;
        }
        container.innerHTML = incoming.innerHTML;
      } catch (_error) {
        if (requestId !== this.recommendationRequest) return;
        if (section) section.hidden = true;
        container.innerHTML = '';
      }
    }

    initializeAcceleratedCheckout() {
      if (window.Shopify?.PaymentButton && typeof window.Shopify.PaymentButton.init === 'function') {
        window.Shopify.PaymentButton.init();
      }

      const wallets = this.querySelector('[data-frenly-wallets]');
      if (!wallets) {
        this.clearWalletWatcher();
        return;
      }

      this.clearWalletWatcher();

      const walletIsReady = () => {
        const skeleton = wallets.querySelector('.wallet-cart-grid--skeleton, .wallet-cart-button__skeleton');
        const renderedControl = wallets.querySelector([
          'shop-pay-wallet-button',
          'paypal-wallet-button',
          'google-pay-wallet-button',
          'apple-pay-wallet-button',
          'iframe',
          'button:not([disabled])',
          '.wallet-cart-button:not(.wallet-cart-button__skeleton):not([disabled])',
          '[data-shopify-buttoncontainer]:not(.wallet-cart-grid--skeleton)',
        ].join(','));
        return !skeleton && Boolean(renderedControl);
      };

      const revealReadyWallets = () => {
        if (!wallets.isConnected || !walletIsReady()) return false;
        wallets.dataset.walletState = 'ready';
        window.clearTimeout(this.walletTimer);
        this.walletTimer = null;
        this.walletObserver?.disconnect();
        this.walletObserver = null;

        if (this.walletExposureSequence !== this.openSequence) {
          this.walletExposureSequence = this.openSequence;
          this.emitCommerceEvent('wallet_exposure', { placement: 'cart_drawer' });
        }
        return true;
      };

      if (revealReadyWallets()) return;

      if (wallets.dataset.walletState !== 'unavailable') {
        wallets.dataset.walletState = 'loading';
      }

      this.walletObserver = new MutationObserver(() => revealReadyWallets());
      this.walletObserver.observe(wallets, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'disabled', 'hidden', 'style'],
      });

      this.walletTimer = window.setTimeout(() => {
        this.walletTimer = null;
        if (!revealReadyWallets() && wallets.isConnected) {
          wallets.dataset.walletState = 'unavailable';
        }
      }, 6000);
    }

    clearWalletWatcher() {
      window.clearTimeout(this.walletTimer);
      this.walletTimer = null;
      this.walletObserver?.disconnect();
      this.walletObserver = null;
    }

    emitCommerceEvent(eventName, details = {}) {
      document.dispatchEvent(
        new CustomEvent('frenly:commerce', {
          detail: {
            event: eventName,
            component: 'cart_drawer',
            ...details,
          },
        })
      );
    }
  }

  if (!customElements.get('cart-drawer')) {
    customElements.define('cart-drawer', FrenlyCartDrawer);
  } else {
    console.error(
      '[Frenly cart drawer] <cart-drawer> is already registered. Disable Dawn cart-drawer.js when the Frenly feature flag is enabled.'
    );
  }

  if (!window.__frenlyCartDrawerGlobalListeners) {
    window.__frenlyCartDrawerGlobalListeners = true;

    document.addEventListener(
      'keydown',
      (event) => {
        const drawer = document.querySelector(DRAWER_SELECTOR);
        if (event.key !== 'Escape' || !drawer?.isOpen) return;

        event.preventDefault();
        drawer.close();
      },
      true
    );

    document.addEventListener(
      'click',
      (event) => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const trigger = event.target.closest(HEADER_TRIGGER_SELECTOR);
        if (!trigger) return;

        const drawer = document.querySelector(DRAWER_SELECTOR);
        if (!drawer?.isEnabled) return;

        event.preventDefault();
        drawer.open(trigger);
      },
      true
    );

    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (
          !(form instanceof HTMLFormElement) ||
          !form.matches('[data-frenly-recommendation-form]') ||
          form.hasAttribute('data-frenly-cart-bypass')
        ) return;

        const drawer = document.querySelector(DRAWER_SELECTOR);
        if (!drawer?.isEnabled) return;

        const actionPath = normalizePath(form.getAttribute('action') || '');
        const addPath = normalizePath(drawer.dataset.cartAddUrl);
        if (!actionPath || actionPath !== addPath) return;

        event.preventDefault();
        event.stopPropagation();
        drawer.handleAddForm(form, event.submitter);
      },
      true
    );
  }
})();
